/**
 * recordingDeleteModalButtons.tsx
 */
import Container from '../../atoms/container/modalButtons';
import Direction from '@shared/types/enums/direction';
import EpgEvent from '@shared/classes/epgEvent';
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import List from '../../sections/list/list';
import ModalButton from '../buttons/ui/modalButton';
import Rail from '@shared/classes/rail';
import lodash from 'lodash';
import { clearModal } from '@shared/store/slices/modal.slice';
import { popFocus, pushFocus } from '@shared/store/slices/focus.slice';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { recordingSelector } from '@shared/store/slices/recordings.slice';
import { reload } from '@shared/store/slices/floatingMenu.slice';
import { removeRecordingRequest } from '@shared/api/recording/recordingsRequest';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useMemo } from 'react';
import {
  selectCurrentParams,
  selectParamsStack,
  updateParams,
  updateStack,
} from '@shared/store/slices/router.slice';

/**
 * RecordingDeleteModalButtons component props interface.
 */
interface Props {
  asset: EpgEvent;
}

/**
 * Displays the recording delete modal buttons.
 */
export default function RecordingDeleteModalButtons({ asset }: Props) {
  const dispatch = useAppDispatch();
  const { currentRecording, identifiers } = useAppSelector(recordingSelector);
  const currentParams = useAppSelector(selectCurrentParams);
  const stack = useAppSelector(selectParamsStack);

  // Memoized values

  const { backLiteral, deleteLiteral } = useMemo(() => {
    const backLiteral = 'MODAL.RECORDING.BACK';
    const deleteLiteral = 'MODAL.RECORDING.DELETE'; // TBD: Change literal based on content to be removed (_SEASON or _EPISODE)
    return { backLiteral, deleteLiteral };
  }, []);

  // Effect hooks

  useEffect(() => {
    dispatch(pushFocus(Focus.MODAL_BUTTONS));
    return () => {
      dispatch(popFocus());
    };
  }, []);

  // Actions

  const backAction = useCallback(() => {
    dispatch(clearModal());
  }, []);

  const deleteAction = useCallback(async () => {
    const recording = identifiers.find(
      (item) => item.code === currentRecording?.code
    );
    const { modal } = store.getState();
    if (recording?.deleteId) {
      removeRecordingRequest(
        recording.deleteId,
        (modal?.params as { recordingType: boolean })?.recordingType,
        recording.code
      ).then(() => {
        filterRecording();
        dispatch(reload());
      });
    }
    dispatch(clearModal());
  }, [currentRecording, identifiers]);

  // Logic callbacks

  const filterRecording = useCallback(() => {
    // If recording is from season extended, remove current item
    if (currentParams?.items) {
      let items = lodash.cloneDeep(currentParams.items) as Event[];
      // Remove item
      if (items?.length && items.length > 0) {
        items = items.filter((item) => item?.id !== asset?.id);
      }
      dispatch(updateParams({ items }));
      // Remove season recording if no more items found
      if (!items?.length) {
        const stackParams = stack;
        const isSeasonRecord =
          currentParams?.isRecord && currentParams?.isSeason;
        if (stackParams?.length && isSeasonRecord) {
          // Generate new current rail
          const currentRail = lodash.cloneDeep(
            stackParams[0]?.currentRail
          ) as Rail;
          currentRail.data = currentRail?.data?.filter(
            (item) => item?.id !== currentParams?.id
          );
          // Generate new rails
          const rails = (lodash.cloneDeep(stackParams[0].rails) as Rail[])
            .map((rail) => (rail.id === currentRail.id ? currentRail : rail))
            .filter((rail) => rail.data.length);
          // Get new indices
          const length = currentRail?.data?.length || 0;
          const horizontalIndex =
            length > 0
              ? ((stackParams[0]?.horizontalIndex as number) || 1) - 1
              : 0;
          const verticalIndex =
            length === 0
              ? Math.min(
                  rails?.length - 1,
                  (stackParams[0]?.verticalIndex as number) || 0
                )
              : stackParams[0]?.verticalIndex;
          // Update stacked params
          dispatch(
            updateStack({
              index: 0,
              params: {
                ...stackParams[0],
                currentRail,
                rails,
                horizontalIndex,
                verticalIndex,
              },
            })
          );
          dispatch(popRoute());
        }
      }
    }
  }, [currentParams, stack]);

  return (
    <Container>
      <List direction={Direction.VERTICAL} focus={Focus.MODAL_BUTTONS}>
        <ModalButton
          index={0}
          releaseAction={deleteAction}
          text={deleteLiteral}
        />
        <ModalButton index={1} releaseAction={backAction} text={backLiteral} />
      </List>
    </Container>
  );
}
